import { type OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { differenceInMinutes, parseISO } from "date-fns";
import { useMemo } from "react";

import { TimeRangeLabel } from "../../../DateTime/TimeRangeLabel";
import { formatHoursDurationCompact } from "../../../utils/formatHoursDurationCompact";
import { ShiftInfoListItemContent } from "../../Bundle/BottomSheet/ShiftInfoListItemContent";
import { type TimeSlot as TimeSlotType } from "../../Open/types";
import { TimeSlotPill } from "../../TimeSlotPill";
import { type ModalShiftData } from "../../types";
import type { DoubleShiftPay, ShiftDouble } from "../types";
import { CompanionCard } from "./CompanionCard";

interface ShiftDoubleBottomSheetContentProps {
  shift: ModalShiftData;
  double: ShiftDouble;
  workplaceTimezone?: string;
  selectedCompanionId: string;
  onSelectCompanion: (companionId: string) => void;
}

interface DoubleSummary {
  combinedStartIso: string;
  combinedEndIso: string;
  totalHours: number;
  totalPayInMinorUnits: number | undefined;
}

function computeDoubleSummary(
  shift: ModalShiftData,
  companion: OpenShift,
  anchorPay: DoubleShiftPay | undefined,
  companionPay: DoubleShiftPay | undefined
): DoubleSummary {
  const anchorStart = shift.attributes.start;
  const anchorEnd = shift.attributes.end;
  const companionStart = companion.attributes.start;
  const companionEnd = companion.attributes.end;

  const combinedStartIso = anchorStart < companionStart ? anchorStart : companionStart;
  const combinedEndIso = anchorEnd > companionEnd ? anchorEnd : companionEnd;

  const totalHours = differenceInMinutes(parseISO(combinedEndIso), parseISO(combinedStartIso)) / 60;

  const totalPayInMinorUnits =
    isDefined(anchorPay?.totalPayInMinorUnits) && isDefined(companionPay?.totalPayInMinorUnits)
      ? anchorPay.totalPayInMinorUnits + companionPay.totalPayInMinorUnits
      : undefined;

  return { combinedStartIso, combinedEndIso, totalHours, totalPayInMinorUnits };
}

function HorizontalRule() {
  return <Box sx={(theme) => ({ borderTop: `1px solid ${theme.palette.divider}`, marginX: 6 })} />;
}

export function ShiftDoubleBottomSheetContent(props: ShiftDoubleBottomSheetContentProps) {
  const { shift, double, workplaceTimezone, selectedCompanionId, onSelectCompanion } = props;

  const { companionShifts, anchorShiftPay, companionShiftPays } = double;

  const hasMultipleCompanions = companionShifts.length > 1;

  const selectedCompanion = companionShifts.find((c) => c.id === selectedCompanionId);
  const selectedCompanionPay = selectedCompanion
    ? companionShiftPays?.get(selectedCompanion.id)
    : undefined;

  const summary = useMemo(() => {
    if (!selectedCompanion) {
      return undefined;
    }

    return computeDoubleSummary(shift, selectedCompanion, anchorShiftPay, selectedCompanionPay);
  }, [shift, selectedCompanion, anchorShiftPay, selectedCompanionPay]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      {/* ── Anchor shift ── */}
      <Stack sx={{ paddingX: 6, paddingTop: 6, paddingBottom: 6 }} spacing={3}>
        <Text semibold variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          The shift you selected
        </Text>

        <Box
          sx={(theme) => ({
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: `${theme.shape?.borderRadius ?? 2}px`,
            backgroundColor: theme.palette.background.quaternary,
            paddingX: 5,
            paddingY: 4,
          })}
        >
          <Stack spacing={3}>
            <TimeSlotPill timeSlot={shift.attributes.timeSlot as TimeSlotType} />
            <Stack direction="row" alignItems="center">
              <ShiftInfoListItemContent
                timeSlot={shift.attributes.timeSlot}
                startIso={shift.attributes.start}
                endIso={shift.attributes.end}
                payInMinorUnits={anchorShiftPay?.totalPayInMinorUnits}
                workplaceTimezone={workplaceTimezone}
              />
            </Stack>
          </Stack>
        </Box>
      </Stack>

      <HorizontalRule />

      {/* ── Double shift(s) ── */}
      <Stack sx={{ paddingX: 6, paddingTop: 6, paddingBottom: 6 }} spacing={4}>
        {hasMultipleCompanions ? (
          <Text semibold variant="body1">
            Pick a double shift
          </Text>
        ) : (
          <Text semibold variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            Double shift
          </Text>
        )}

        <Stack spacing={4}>
          {companionShifts.map((companion) => (
            <CompanionCard
              key={companion.id}
              companion={companion}
              anchorStart={shift.attributes.start}
              isSelectable={hasMultipleCompanions}
              isSelected={selectedCompanionId === companion.id}
              pay={companionShiftPays?.get(companion.id)}
              workplaceTimezone={workplaceTimezone}
              onSelect={() => {
                onSelectCompanion(companion.id);
              }}
            />
          ))}
        </Stack>
      </Stack>

      {/* ── Summary receipt (multi-companion only) ── */}
      {hasMultipleCompanions && isDefined(summary) && (
        <>
          <HorizontalRule />

          <Box sx={{ paddingX: 6, paddingTop: 6, paddingBottom: 6 }}>
            <Box
              sx={(theme) => ({
                borderRadius: `${theme.shape?.borderRadius ?? 2}px`,
                border: `1px solid ${theme.palette.grey[300]}`,
                backgroundColor: theme.palette.background.quaternary,
                padding: 5,
              })}
            >
              <Stack spacing={3}>
                <Text semibold variant="body1">
                  Your double
                </Text>

                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <TimeRangeLabel
                      semibold
                      variant="body1"
                      dateRange={{
                        startDate: parseISO(summary.combinedStartIso),
                        endDate: parseISO(summary.combinedEndIso),
                      }}
                      timezone={workplaceTimezone}
                    />
                    <Text
                      semibold
                      variant="body2"
                      sx={(theme) => ({ color: theme.palette.text.secondary })}
                    >
                      {formatHoursDurationCompact(summary.totalHours, { omitZeroMinutes: true })}
                    </Text>
                  </Stack>

                  <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                    2 back-to-back shifts
                  </Text>
                </Stack>

                {isDefined(summary.totalPayInMinorUnits) && (
                  <>
                    <Box sx={(theme) => ({ borderTop: `1px solid ${theme.palette.divider}` })} />
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                      <Text semibold variant="body1">
                        Total earnings
                      </Text>
                      <Text semibold variant="h5">
                        {formatCentsAsUsd(summary.totalPayInMinorUnits)}
                      </Text>
                    </Stack>
                  </>
                )}
              </Stack>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
